import React, { useState, useMemo, useCallback, useRef } from 'react'
import { type Dispatch, type SetStateAction } from 'react'
import Plot from 'react-plotly.js'
import moment, { tz } from 'moment'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import styled from 'styled-components'

import Legend from 'pages/Device/AwairRthPanel/Legend'

import { buildAwairLayout, buildTraces, updateLayoutOnResetZoom, updateTracesVisibility } from 'pages/Device/AwairRthPanel/chartBuilder'

import Brand from 'helpers/brand'
import { config } from 'helpers/plotly/config'
import { chartHeight, xValuePositionInPixel } from 'helpers/plotly/utils'

import { SelectedLegendItems } from 'types/Awair'
import { NoData, screenSize } from 'styles/shared'
import { color } from 'styles/colors'

type AwairRthChartProps = {
  awairEvents: Record<string, unknown>[]
  thermostatEvents?: Record<string, unknown>
  displayIndoorUnit?: boolean
  isZoomed: boolean
  date: string
  fromTime: string
  toTime: string
  serverErrorOccurred: boolean
  setDisplayResetZoomCallback: (val: boolean) => void
  timeZone?: string
  selectedLegendItems: SelectedLegendItems
  setSelectedLegendItemsCallback: Dispatch<SetStateAction<SelectedLegendItems>>
}

const AwairRthChart = ({ awairEvents, isZoomed, fromTime, toTime, thermostatEvents, displayIndoorUnit = false, date, serverErrorOccurred, setDisplayResetZoomCallback, timeZone, selectedLegendItems, setSelectedLegendItemsCallback }: AwairRthChartProps) => {
  const [isChartHovered, setIsChartHovered] = useState(false)
  const [timeAtCursor, setTimeAtCursor] = useState<number | null>(null)
  const [mouseCursorPosition, setMouseCursorPosition] = useState<number | null>(null)
  const [dataAtCursor, setDataAtCursor] = useState<unknown[] | null>(null)
  const [revision, setRevision] = useState(1)
  const graphRef = useRef<{ _fullLayout?: unknown } | null>(null)
  const isDateWithin30Days = moment(date, 'YYYY-MM-DD').startOf('day').isSameOrAfter(moment().subtract(30, 'days').startOf('day'))
  const displayChart = isDateWithin30Days && awairEvents.length > 0 && !serverErrorOccurred

  const traces = useMemo(() => buildTraces(awairEvents, thermostatEvents as Record<string, unknown>, displayIndoorUnit, timeZone as string), [awairEvents, displayIndoorUnit, thermostatEvents, timeZone])

  const updateTraces = useMemo(() => {
    const data = updateTracesVisibility(traces, selectedLegendItems)
    return data
  }, [traces, selectedLegendItems])

  const setSelectedLegendItems = useCallback((currentSelectedLegendItems) => {
    setRevision(revision + 1)
    setSelectedLegendItemsCallback(currentSelectedLegendItems)
  }, [revision, setSelectedLegendItemsCallback])

  const layout = useMemo(() => buildAwairLayout(fromTime, toTime, updateTraces), [fromTime, updateTraces, toTime])

  const updatedLayout = useMemo(() => {
    if (!layout) { return layout }

    const updatedLayout = updateLayoutOnResetZoom(isZoomed, graphRef.current?._fullLayout, layout) // resets the zoom

    return updatedLayout
  }, [layout, isZoomed])

  const onInitialized = (_: unknown, element: unknown) => {
    graphRef.current = element as { _fullLayout?: unknown }
  }

  const onHover = (e) => {
    e.event.preventDefault()
    if (e.xvals.length > 0 && e.xvals[0]) {
      const momentZone = tz.zone(timeZone ?? '')
      const time = Number(e.xvals[0])
      const timeZoneOffSetInMins = momentZone && momentZone.parse(time)
      setMouseCursorPosition(xValuePositionInPixel(e.points[0].xaxis, e.points[0].x, graphRef.current?._fullLayout)) // offsetX is the mouse hover position in the graph updating when mouse move

      if (timeZoneOffSetInMins) {
        const timeAtCursor = (time + ((timeZoneOffSetInMins * 60 * 1000))) // As plotly does not support timezone we are manually converting
        setTimeAtCursor(timeAtCursor)
        setIsChartHovered(true)
        setDataAtCursor(e.points)
      }
    }
  }

  const onUnhover = (e) => {
    e.event.preventDefault()
    setTimeAtCursor(null)
    setDataAtCursor(null)
    setIsChartHovered(false)
  }

  const onRelayout = useCallback(({ autosize }) => {
    if (!autosize) {
      setRevision(revision + 1)
      setDisplayResetZoomCallback(true)
    }
  }, [revision, setDisplayResetZoomCallback])

  return (
    <>
      {(() => {
        if (displayChart) {
          return (
            <>
              {updatedLayout &&
                <>
                  <S.ChartContainer>
                    <S.Plot
                      divId={'awair-plot'}
                      layout={updatedLayout}
                      data={updateTraces}
                      config={config as Record<string, unknown>}
                      revision={revision}
                      onHover={onHover}
                      onUnhover={onUnhover}
                      onInitialized={onInitialized}
                      onRelayout={onRelayout}
                      useResizeHandler
                    />
                    {isChartHovered && (
                      <S.ToolTipCrossHair left={mouseCursorPosition} width={2} height={chartHeight(graphRef.current) + 10}>
                        <S.ArrowDropUpIcon $top={chartHeight(graphRef.current) - 25} />
                      </S.ToolTipCrossHair>
                    )}

                  </S.ChartContainer>
                  <Legend
                    setSelectedLegendItemsCallback={setSelectedLegendItems}
                    {...{ selectedLegendItems, dataAtCursor: dataAtCursor as Plotly.PlotDatum[] | null, isChartHovered, timeAtCursor, displayIndoorUnit }}
                    timeZone={timeZone ?? ''}
                  />
                </>
              }

            </>
          )
        } else if (!isDateWithin30Days) {
          return (
            <S.NoData>
              <h1>No Data</h1>
              <h2>The date is more than 30 days old.</h2>
              <h2>Please select a date within the past 30 days.</h2>
            </S.NoData>
          )
        } else if (serverErrorOccurred) {
          return (
            <S.NoData>
              <h1>Server Error</h1>
              <h2>An error has occurred on the server. Please contact {Brand.productName()} support.</h2>
            </S.NoData>
          )
        } else {
          return (
            <S.NoData>
              <h1>No Data</h1>
              <h2>There is no data for this day.</h2>
            </S.NoData>
          )
        }
      })()}
    </>
  )
}

export default AwairRthChart

const minHeight = '400px'
const S = {
  ChartContainer: styled.div`
   text-align: center;
   position: relative;
   margin: 0 auto 40px auto;
   width: 95%;
   pointer-events: none;
 `,
  Plot: styled(Plot)`
    width: 100%;
    min-height: ${minHeight};
  `,
  ArrowDropUpIcon: styled(ArrowDropUpIcon).withConfig({
    shouldForwardProp: (prop) => !['$top'].includes(prop)
  })<{ $top?: number }>`
    font-size: 75px;
    margin-left: -38px;
    position: absolute;
    pointer-events: none;
    top:  ${({ $top }) => (`${$top}px`)};
  `,
  ToolTipCrossHair: styled.div.withConfig({
    shouldForwardProp: (prop) => !['left', 'width', 'height'].includes(prop)
  })<{ left?: number | null; width?: number; height?: number }>`
    background-color: ${color('rthLegendBorderHover')};
    height: ${({ height }) => (`${height}px`)};
    width: ${({ width }) => (`${width}px`)};
    pointer-events: none;
    position: absolute;
    left: ${({ left, width }) => (`${(left ?? 0) - ((width ?? 0) * 0.5)}px`)};
    top: 0;
    z-index: 1;
  `,
  NoData: styled(NoData)`
    min-height: ${minHeight};

    @media ${screenSize.small} {
      min-height: 0;
      padding: 3.3em;
    }
  `
}
